import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  CheckboxGroup,
  Input,
  Select,
  SelectItem,
  Spinner,
  Divider,
  Chip,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  saveMarketComparisonConfig,
  loadMarketComparisonConfig,
  DEFAULT_MARKET_COMPARISON_CONFIG,
  type MarketComparisonConfig,
  type MatchVariable,
  type VariableGroups,
} from 'common/src/api/market-comparison-config';
import { VALID_FORMATS } from 'common/src/utils/definitions';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import useUserStore from 'common/src/stores/useUserStore';
import { supabase } from 'common/src/supabase';
import { useTranslation } from 'common/src/hooks/useTranslation';

// Converts a format type + weight into the weight_kg value used in market_data queries
function toWeightCondition(formatType: string, weight: number): string {
  const standardFormat = `${formatType}-${weight}KG`;
  if (standardFormat.includes('CAJA')) {
    return standardFormat.split('-')[1];
  } else if (standardFormat.includes('IN')) {
    return standardFormat.replace('-', '+');
  }
  return standardFormat;
}

// Converts a weight_kg value back to a display label
function formatWeightLabel(weightCondition: string): string {
  // "5KG" -> "CAJA 5KG", "2IN1+5KG" -> "2IN1 5KG"
  if (weightCondition.includes('+')) {
    return weightCondition.replace('+', ' ');
  }
  return `CAJA ${weightCondition}`;
}

const VARIABLE_LABELS: Record<MatchVariable, string> = {
  variety: 'Variedad',
  format: 'Formato/Peso',
  caliber: 'Calibre',
  color: 'Color',
};

interface GroupEditorProps {
  variableKey: keyof VariableGroups;
  label: string;
  groups: Record<string, string[]>;
  onChange: (groups: Record<string, string[]>) => void;
  t: (key: string) => string;
}

const GroupEditor = ({
  variableKey,
  label,
  groups,
  onChange,
  t,
}: GroupEditorProps) => {
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupValues, setNewGroupValues] = useState('');

  const handleAddGroup = () => {
    const name = newGroupName.trim();
    const values = newGroupValues
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    if (!name || values.length === 0) return;
    if (groups[name]) {
      toast.warning(t('Ya existe un grupo con ese nombre'));
      return;
    }

    onChange({ ...groups, [name]: values });
    setNewGroupName('');
    setNewGroupValues('');
  };

  const handleDeleteGroup = (groupName: string) => {
    const updated = { ...groups };
    delete updated[groupName];
    onChange(updated);
  };

  return (
    <div className='mt-3'>
      <p className='text-sm font-semibold mb-2'>{label}</p>

      {Object.entries(groups).length > 0 && (
        <div className='space-y-2 mb-3'>
          {Object.entries(groups).map(([groupName, values]) => (
            <div key={groupName} className='flex items-center gap-2 flex-wrap'>
              <span className='text-sm font-medium min-w-[80px]'>
                {groupName}:
              </span>
              <div className='flex gap-1 flex-wrap'>
                {values.map((v) => (
                  <Chip key={v} size='sm' variant='flat'>
                    {v}
                  </Chip>
                ))}
              </div>
              <Button
                size='sm'
                color='danger'
                variant='light'
                isIconOnly
                onPress={() => handleDeleteGroup(groupName)}
              >
                ✕
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className='flex gap-2 items-end'>
        <Input
          size='sm'
          label={t('Nombre del grupo')}
          value={newGroupName}
          onValueChange={setNewGroupName}
          className='max-w-[150px]'
        />
        <Input
          size='sm'
          label={t('Valores (separados por coma)')}
          value={newGroupValues}
          onValueChange={setNewGroupValues}
          className='flex-1'
          placeholder='ej: 40, 42, 45'
        />
        <Button
          size='sm'
          variant='flat'
          onPress={handleAddGroup}
          isDisabled={!newGroupName.trim() || !newGroupValues.trim()}
        >
          {t('Agregar')}
        </Button>
      </div>
    </div>
  );
};

interface FormatGroupEditorProps {
  groups: Record<string, string[]>;
  onChange: (groups: Record<string, string[]>) => void;
  t: (key: string) => string;
}

const FormatGroupEditor = ({ groups, onChange, t }: FormatGroupEditorProps) => {
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [weight, setWeight] = useState('');
  const [pendingValues, setPendingValues] = useState<string[]>([]);

  const handleAddValue = () => {
    if (!selectedFormat || !weight) return;
    const weightCondition = toWeightCondition(
      selectedFormat,
      parseFloat(weight)
    );
    if (!pendingValues.includes(weightCondition)) {
      setPendingValues([...pendingValues, weightCondition]);
    }
    setSelectedFormat('');
    setWeight('');
  };

  const handleRemovePending = (value: string) => {
    setPendingValues(pendingValues.filter((v) => v !== value));
  };

  const handleAddGroup = () => {
    const name = newGroupName.trim();
    if (!name || pendingValues.length === 0) return;
    if (groups[name]) {
      toast.warning(t('Ya existe un grupo con ese nombre'));
      return;
    }
    onChange({ ...groups, [name]: pendingValues });
    setNewGroupName('');
    setPendingValues([]);
  };

  const handleDeleteGroup = (groupName: string) => {
    const updated = { ...groups };
    delete updated[groupName];
    onChange(updated);
  };

  return (
    <div className='mt-3'>
      <p className='text-sm font-semibold mb-2'>{t('Formato/Peso')}</p>

      {Object.entries(groups).length > 0 && (
        <div className='space-y-2 mb-3'>
          {Object.entries(groups).map(([groupName, values]) => (
            <div key={groupName} className='flex items-center gap-2 flex-wrap'>
              <span className='text-sm font-medium min-w-[80px]'>
                {groupName}:
              </span>
              <div className='flex gap-1 flex-wrap'>
                {values.map((v) => (
                  <Chip key={v} size='sm' variant='flat'>
                    {formatWeightLabel(v)}
                  </Chip>
                ))}
              </div>
              <Button
                size='sm'
                color='danger'
                variant='light'
                isIconOnly
                onPress={() => handleDeleteGroup(groupName)}
              >
                ✕
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className='space-y-2'>
        <div className='flex gap-2 items-end'>
          <Input
            size='sm'
            label={t('Nombre del grupo')}
            value={newGroupName}
            onValueChange={setNewGroupName}
            className='max-w-[150px]'
          />
          <Select
            size='sm'
            label={t('Formato')}
            selectedKeys={selectedFormat ? [selectedFormat] : []}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as string;
              setSelectedFormat(val || '');
            }}
            className='max-w-[150px]'
          >
            {VALID_FORMATS.map((f) => (
              <SelectItem key={f}>{f}</SelectItem>
            ))}
          </Select>
          <Input
            size='sm'
            type='number'
            label={t('Peso (KG)')}
            value={weight}
            onValueChange={setWeight}
            className='max-w-[120px]'
            min={0}
          />
          <Button
            size='sm'
            color='default'
            variant='flat'
            onPress={handleAddValue}
            isDisabled={!selectedFormat || !weight}
          >
            +
          </Button>
        </div>

        {pendingValues.length > 0 && (
          <div className='flex gap-1 flex-wrap items-center'>
            <span className='text-sm text-default-500'>{t('Valores')}:</span>
            {pendingValues.map((v) => (
              <Chip
                key={v}
                size='sm'
                variant='flat'
                onClose={() => handleRemovePending(v)}
              >
                {formatWeightLabel(v)}
              </Chip>
            ))}
          </div>
        )}

        <Button
          size='sm'
          variant='flat'
          onPress={handleAddGroup}
          isDisabled={!newGroupName.trim() || pendingValues.length === 0}
        >
          {t('Agregar grupo')}
        </Button>
      </div>
    </div>
  );
};

export const MarketComparisonConfigSection = () => {
  const { t } = useTranslation('settings');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<MarketComparisonConfig>(
    DEFAULT_MARKET_COMPARISON_CONFIG
  );
  const isInitialLoad = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load config
  useEffect(() => {
    const loadConfig = async () => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason)
        return;

      setLoading(true);
      isInitialLoad.current = true;
      try {
        const session = await supabase.auth.getSession();
        const saved = await loadMarketComparisonConfig(
          userProfile.exporter_id,
          selectedSeason,
          selectedSpecies.id,
          session.data.session
        );
        setConfig(saved ?? DEFAULT_MARKET_COMPARISON_CONFIG);
      } catch (error) {
        console.error('Error loading market comparison config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [userProfile?.exporter_id, selectedSpecies?.id, selectedSeason]);

  const persistConfig = useCallback(
    async (configToSave: MarketComparisonConfig) => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason)
        return;

      setSaving(true);
      try {
        const session = await supabase.auth.getSession();
        const success = await saveMarketComparisonConfig(
          userProfile.exporter_id,
          selectedSeason,
          selectedSpecies.id,
          configToSave,
          session.data.session
        );

        if (!success) {
          toast.error(t('Error al guardar configuración'));
        }
      } catch (error) {
        console.error('Error saving market comparison config:', error);
        toast.error(t('Error al guardar configuración'));
      } finally {
        setSaving(false);
      }
    },
    [userProfile?.exporter_id, selectedSpecies?.id, selectedSeason, t]
  );

  // Auto-save on config changes (debounced for number inputs)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    if (loading) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      persistConfig(config);
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [config, loading, persistConfig]);

  const updateVariableGroups = (
    variableKey: keyof VariableGroups,
    groups: Record<string, string[]>
  ) => {
    setConfig((prev) => ({
      ...prev,
      variable_groups: {
        ...prev.variable_groups,
        [variableKey]: Object.keys(groups).length > 0 ? groups : undefined,
      },
    }));
  };

  if (loading) {
    return (
      <div className='flex justify-center p-8'>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <p className='text-lg font-bold'>{t('Configuración Comparación de Mercado')}</p>
        {saving && (
          <span className='text-sm text-default-400'>
            {t('Guardando...')}
          </span>
        )}
      </div>

      <Card>
        <CardBody className='gap-6'>
          {/* Time Window */}
          <div>
            <p className='text-md font-semibold mb-3'>
              {t('Ventana de Tiempo')}
            </p>
            <p className='text-sm text-default-500 mb-3'>
              {t(
                'Define el rango de días alrededor de la fecha ETA para buscar datos de mercado'
              )}
            </p>
            <div className='flex gap-4'>
              <Input
                type='number'
                label={t('Días antes')}
                value={String(config.window_days_before)}
                onValueChange={(v) =>
                  setConfig((prev) => ({
                    ...prev,
                    window_days_before: Math.max(0, parseInt(v) || 0),
                  }))
                }
                min={0}
                className='max-w-[150px]'
              />
              <Input
                type='number'
                label={t('Días después')}
                value={String(config.window_days_after)}
                onValueChange={(v) =>
                  setConfig((prev) => ({
                    ...prev,
                    window_days_after: Math.max(0, parseInt(v) || 0),
                  }))
                }
                min={0}
                className='max-w-[150px]'
              />
            </div>
          </div>

          <Divider />

          {/* Match Variables */}
          <div>
            <p className='text-md font-semibold mb-3'>
              {t('Variables de Comparación')}
            </p>
            <p className='text-sm text-default-500 mb-3'>
              {t(
                'Selecciona qué variables usar para encontrar datos de mercado comparables'
              )}
            </p>
            <CheckboxGroup
              value={config.match_variables}
              onValueChange={(values) =>
                setConfig((prev) => ({
                  ...prev,
                  match_variables: values as MatchVariable[],
                }))
              }
              orientation='horizontal'
              className='gap-4'
            >
              {(
                Object.entries(VARIABLE_LABELS) as [MatchVariable, string][]
              ).map(([key, label]) => (
                <Checkbox key={key} value={key}>
                  {t(label)}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </div>

          <Divider />

          {/* Variable Groups */}
          <div>
            <p className='text-md font-semibold mb-2'>
              {t('Agrupaciones de Variables')}
            </p>
            <p className='text-sm text-default-500 mb-3'>
              {t(
                'Define grupos de valores equivalentes para cada variable. Los valores dentro de un grupo se consideran iguales al comparar.'
              )}
            </p>

            {config.match_variables.length === 0 && (
              <p className='text-sm text-default-400 italic'>
                {t(
                  'Selecciona al menos una variable de comparación para configurar agrupaciones'
                )}
              </p>
            )}

            {config.match_variables.includes('format') && (
              <FormatGroupEditor
                groups={config.variable_groups?.format ?? {}}
                onChange={(groups) => updateVariableGroups('format', groups)}
                t={t}
              />
            )}

            {(Object.entries(VARIABLE_LABELS) as [MatchVariable, string][]).map(
              ([key, label]) => {
                if (key === 'format') return null;
                if (!config.match_variables.includes(key)) return null;
                return (
                  <GroupEditor
                    key={key}
                    variableKey={key}
                    label={t(label)}
                    groups={config.variable_groups?.[key] ?? {}}
                    onChange={(groups) => updateVariableGroups(key, groups)}
                    t={t}
                  />
                );
              }
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
